/**
 * Enumerations and constant lists shared by the API, the database schema and the web app
 * (SRS §6, FR-LKP-4, IMPORT_MAPPING §2). Values are UPPER_SNAKE; labels are for display only.
 */
/** Builds a `{ VALUE: 'VALUE' }` object so call sites can write `PaymentMode.BANK`. */
function valueMap(values) {
    return Object.freeze(Object.fromEntries(values.map((v) => [v, v])));
}
// ── Access domain ────────────────────────────────────────────────────────────
export const ACCOUNT_STATUSES = ['ACTIVE', 'DISABLED'];
export const AccountStatus = valueMap(ACCOUNT_STATUSES);
export const ACCOUNT_STATUS_LABELS = {
    ACTIVE: 'Active',
    DISABLED: 'Disabled',
};
/**
 * How an account signs in (FR-AUTH-9, ADR-091): derived from the login email's domain against the
 * Google Workspace domains setting, never stored.
 */
export const SIGN_IN_METHODS = ['GOOGLE', 'PASSWORD'];
export const SignInMethod = valueMap(SIGN_IN_METHODS);
/** One-time password link purposes (FR-AUTH-14, SRS §6.1 `auth_tokens`). */
export const AUTH_TOKEN_PURPOSES = ['SET_PASSWORD', 'RESET_PASSWORD'];
export const AuthTokenPurpose = valueMap(AUTH_TOKEN_PURPOSES);
// ── Employee ─────────────────────────────────────────────────────────────────
/** Derived, never stored (FR-EMP-6). */
export const EMPLOYEE_STATUSES = ['UPCOMING', 'ACTIVE', 'NOTICE_PERIOD', 'EXITED'];
export const EmployeeStatus = valueMap(EMPLOYEE_STATUSES);
export const EMPLOYEE_STATUS_LABELS = {
    UPCOMING: 'Upcoming',
    ACTIVE: 'Active',
    NOTICE_PERIOD: 'Notice period',
    EXITED: 'Exited',
};
export const GENDERS = ['MALE', 'FEMALE', 'OTHER'];
export const Gender = valueMap(GENDERS);
export const GENDER_LABELS = {
    MALE: 'Male',
    FEMALE: 'Female',
    OTHER: 'Other',
};
export const BLOOD_GROUPS = [
    'A_POS',
    'A_NEG',
    'B_POS',
    'B_NEG',
    'AB_POS',
    'AB_NEG',
    'O_POS',
    'O_NEG',
];
export const BloodGroup = valueMap(BLOOD_GROUPS);
export const BLOOD_GROUP_LABELS = {
    A_POS: 'A+',
    A_NEG: 'A−',
    B_POS: 'B+',
    B_NEG: 'B−',
    AB_POS: 'AB+',
    AB_NEG: 'AB−',
    O_POS: 'O+',
    O_NEG: 'O−',
};
export const TSHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XXXXL'];
export const TshirtSize = valueMap(TSHIRT_SIZES);
// ── Employment (FR-JOB) ──────────────────────────────────────────────────────
export const EMPLOYMENT_TYPES = [
    'PERMANENT',
    'PROBATION',
    'CONTRACTUAL',
    'INTERN',
    'CONSULTANT',
];
export const EmploymentType = valueMap(EMPLOYMENT_TYPES);
export const EMPLOYMENT_TYPE_LABELS = {
    PERMANENT: 'Permanent',
    PROBATION: 'Probation',
    CONTRACTUAL: 'Contractual',
    INTERN: 'Intern',
    CONSULTANT: 'Consultant',
};
export const RESOURCE_USAGES = ['FULL_TIME', 'SHARED', 'PART_TIME'];
export const ResourceUsage = valueMap(RESOURCE_USAGES);
export const RESOURCE_USAGE_LABELS = {
    FULL_TIME: 'Full-time',
    SHARED: 'Shared',
    PART_TIME: 'Part-time',
};
export const EMPLOYMENT_REASONS = [
    'JOINING',
    'CONFIRMATION',
    'PROMOTION',
    'TRANSFER',
    'REDESIGNATION',
    'TYPE_CHANGE',
    'CORRECTION',
];
export const EmploymentReason = valueMap(EMPLOYMENT_REASONS);
export const EMPLOYMENT_REASON_LABELS = {
    JOINING: 'Joining',
    CONFIRMATION: 'Confirmation',
    PROMOTION: 'Promotion',
    TRANSFER: 'Transfer',
    REDESIGNATION: 'Redesignation',
    TYPE_CHANGE: 'Type change',
    CORRECTION: 'Correction',
};
// ── Salary (FR-SAL) ──────────────────────────────────────────────────────────
export const PAYMENT_MODES = ['CASH', 'BANK', 'CASH_AND_BANK'];
export const PaymentMode = valueMap(PAYMENT_MODES);
export const PAYMENT_MODE_LABELS = {
    CASH: 'Cash',
    BANK: 'Bank A/C',
    CASH_AND_BANK: 'Cash + Bank A/C',
};
export const SALARY_REASONS = [
    'JOINING',
    'CONFIRMATION',
    'APPRAISAL',
    'PROMOTION',
    'ADJUSTMENT',
    'CORRECTION',
];
export const SalaryReason = valueMap(SALARY_REASONS);
export const SALARY_REASON_LABELS = {
    JOINING: 'Joining',
    CONFIRMATION: 'Confirmation',
    APPRAISAL: 'Appraisal',
    PROMOTION: 'Promotion',
    ADJUSTMENT: 'Adjustment',
    CORRECTION: 'Correction',
};
/** Pay component kind (ADR-018). Salary components are earnings; payroll adds deductions. */
export const PAY_COMPONENT_KINDS = ['EARNING', 'DEDUCTION'];
export const PayComponentKind = valueMap(PAY_COMPONENT_KINDS);
/**
 * Where a pay component is used (FR-PAY-1, ADR-053): `SALARY` components split a salary's A/C
 * part (salary history, the 100 % rule); `PAYROLL` components appear only in payroll runs.
 */
export const PAY_COMPONENT_SCOPES = ['SALARY', 'PAYROLL'];
export const PayComponentScope = valueMap(PAY_COMPONENT_SCOPES);
// ── Data completeness (FR-DASH-3, T-02A) ─────────────────────────────────────
/** Missing-data flags computed per employee from current data (ADR-037). */
export const COMPLETENESS_FLAGS = [
    'NO_LOGIN_EMAIL',
    'NO_EMERGENCY_CONTACT',
    'NO_NID',
    'BANK_WITHOUT_ACCOUNT',
];
export const CompletenessFlag = valueMap(COMPLETENESS_FLAGS);
export const COMPLETENESS_FLAG_LABELS = {
    NO_LOGIN_EMAIL: 'No login email',
    NO_EMERGENCY_CONTACT: 'No emergency contact',
    NO_NID: 'No NID / Birth ID',
    BANK_WITHOUT_ACCOUNT: 'Bank A/C salary without a bank account',
};
// ── Emergency contact (FR-SELF-2) ────────────────────────────────────────────
/** Suggestions only — relation is free text (IMPORT_MAPPING §2.6). */
export const RELATION_SUGGESTIONS = [
    'Father',
    'Mother',
    'Spouse',
    'Brother',
    'Sister',
    'Son',
    'Daughter',
    'Relative',
    'Friend',
];
// ── Exits (FR-EXIT) ──────────────────────────────────────────────────────────
export const EXIT_TYPES = [
    'RESIGNATION',
    'TERMINATION',
    'CONTRACT_END',
    'RETIREMENT',
    'ABSCONDING',
    'DEATH',
    'OTHER',
];
export const ExitType = valueMap(EXIT_TYPES);
export const EXIT_TYPE_LABELS = {
    RESIGNATION: 'Resignation',
    TERMINATION: 'Termination',
    CONTRACT_END: 'Contract end',
    RETIREMENT: 'Retirement',
    ABSCONDING: 'Absconding',
    DEATH: 'Death',
    OTHER: 'Other',
};
export const SETTLEMENT_STATUSES = ['PENDING', 'IN_PROGRESS', 'PAID', 'NA'];
export const SettlementStatus = valueMap(SETTLEMENT_STATUSES);
export const SETTLEMENT_STATUS_LABELS = {
    PENDING: 'Pending',
    IN_PROGRESS: 'In progress',
    PAID: 'Paid',
    NA: 'N/A',
};
/** Exit clearance answers (assets returned, access revoked, handover done). */
export const YES_NO_NA = ['YES', 'NO', 'NA'];
export const YesNoNa = valueMap(YES_NO_NA);
export const YES_NO_NA_LABELS = {
    YES: 'Yes',
    NO: 'No',
    NA: 'N/A',
};
// ── Documents (FR-DOC) ───────────────────────────────────────────────────────
export const ATTACHMENT_KINDS = ['NID_FRONT', 'NID_BACK', 'PASSPORT', 'OTHER_ID'];
export const AttachmentKind = valueMap(ATTACHMENT_KINDS);
export const ATTACHMENT_KIND_LABELS = {
    NID_FRONT: 'NID front',
    NID_BACK: 'NID back',
    PASSPORT: 'Passport (bio page)',
    OTHER_ID: 'Other ID',
};
export const ATTACHMENT_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/pdf',
];
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
// ── Approvals (ADR-017) ──────────────────────────────────────────────────────
/**
 * Approval types the engine has registered (the inbox filter, `GET /approvals?type=`): Core's
 * Bank & TIN changes, payroll runs (M6), leave, WFH and attendance corrections (M7). Later
 * modules add theirs.
 */
export const APPROVAL_TYPES = [
    'BANK_TAX_CHANGE',
    'PAYROLL_RUN',
    'LEAVE',
    'WFH',
    'ATTENDANCE_REGULARISATION',
];
export const ApprovalType = valueMap(APPROVAL_TYPES);
export const APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'];
export const ApprovalStatus = valueMap(APPROVAL_STATUSES);
export const APPROVAL_STEP_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'SKIPPED'];
export const ApprovalStepStatus = valueMap(APPROVAL_STEP_STATUSES);
export const APPROVER_KINDS = ['ROLE', 'REPORTING_MANAGER', 'USER'];
export const ApproverKind = valueMap(APPROVER_KINDS);
// ── Calendar (EXT-9) ─────────────────────────────────────────────────────────
export const HOLIDAY_TYPES = ['PUBLIC', 'COMPANY', 'OPTIONAL'];
export const HolidayType = valueMap(HOLIDAY_TYPES);
export const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
export const Weekday = valueMap(WEEKDAYS);
export const WEEKDAY_LABELS = {
    SUN: 'Sunday',
    MON: 'Monday',
    TUE: 'Tuesday',
    WED: 'Wednesday',
    THU: 'Thursday',
    FRI: 'Friday',
    SAT: 'Saturday',
};
// ── Effective-dated history (BR-DEL-1) ───────────────────────────────────────
/** History tables whose rows can be voided via `POST /employees/:id/history/:table/:rowId/void`. */
export const VOIDABLE_HISTORY_TABLES = [
    'employment',
    'salary',
    'bank',
    'contact',
    'emergency',
    'reporting',
];
//# sourceMappingURL=enums.js.map